export interface IPFSUploadResult {
  success: boolean;
  ipfsCid?: string;
  error?: string;
  uploadTimestamp: string;
}

export interface IPFSUploadOptions {
  onProgress?: (progress: number) => void;
  timeout?: number;
}

/**
 * Upload encrypted file to IPFS via Pinata API
 */
export const uploadToIPFS = async (
  encryptedData: string,
  fileName: string,
  options: IPFSUploadOptions = {}
): Promise<IPFSUploadResult> => {
  const { onProgress, timeout = 60000 } = options; // Increased timeout to 60 seconds
  const uploadTimestamp = new Date().toISOString();

  try {
    // Validate inputs
    if (!encryptedData || !fileName) {
      throw new Error('Missing required parameters: encryptedData or fileName');
    }

    // Convert encrypted string to blob
    const blob = new Blob([encryptedData], { type: 'application/octet-stream' });
    
    // Validate file size (100MB limit for paid tier)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (blob.size > maxSize) {
      throw new Error(`File size exceeds 100MB limit. Current size: ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
    }

    // Get Pinata credentials
    const pinataJWT = import.meta.env.VITE_PINATA_JWT;
    
    if (!pinataJWT) {
      throw new Error('Pinata JWT not configured. Please add VITE_PINATA_JWT to your environment variables.');
    }

    // Validate JWT format
    if (!pinataJWT.startsWith('eyJ')) {
      throw new Error('Invalid Pinata JWT format. Please check your VITE_PINATA_JWT environment variable.');
    }

    // Create FormData
    const formData = new FormData();
    formData.append('file', blob, fileName);
    
    // Add metadata
    const metadata = JSON.stringify({
      name: fileName,
      keyvalues: {
        encrypted: 'true',
        uploadedAt: uploadTimestamp,
        originalName: fileName,
        app: 'aegis-vault'
      }
    });
    formData.append('pinataMetadata', metadata);

    // Add options for pinning
    const options_data = JSON.stringify({
      cidVersion: 1,
      customPinPolicy: {
        regions: [
          {
            id: 'FRA1',
            desiredReplicationCount: 1
          },
          {
            id: 'NYC1', 
            desiredReplicationCount: 1
          }
        ]
      }
    });
    formData.append('pinataOptions', options_data);

    // Set up progress tracking
    if (onProgress) {
      onProgress(0);
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      // Upload to Pinata using JWT authentication
      const response = await fetch('https://api.pinata.cloud/pinning/pinFileToIPFS', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${pinataJWT}`
        },
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (onProgress) {
        onProgress(100);
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Pinata upload failed: ${response.status} ${response.statusText}`;
        
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.error) {
            errorMessage += `. ${errorJson.error}`;
          }
          if (errorJson.details) {
            errorMessage += `. Details: ${errorJson.details}`;
          }
        } catch {
          errorMessage += `. ${errorText}`;
        }
        
        // Provide specific error messages for common issues
        if (response.status === 401) {
          errorMessage = 'Invalid Pinata JWT token. Please check your VITE_PINATA_JWT environment variable.';
        } else if (response.status === 402) {
          errorMessage = 'Pinata account limit exceeded. Please upgrade your plan or check your usage.';
        } else if (response.status === 413) {
          errorMessage = 'File too large for your Pinata plan. Please reduce file size or upgrade your plan.';
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();

      // Validate response format - Pinata returns IpfsHash
      if (!result.IpfsHash) {
        throw new Error('Invalid Pinata response: missing IpfsHash');
      }

      return {
        success: true,
        ipfsCid: result.IpfsHash,
        uploadTimestamp
      };

    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      
      if (fetchError.name === 'AbortError') {
        throw new Error(`Upload timeout after ${timeout / 1000} seconds. Please try again with a smaller file.`);
      }
      
      throw fetchError;
    }

  } catch (error: any) {
    console.error('IPFS upload error:', error);
    
    return {
      success: false,
      error: error.message || 'Unknown upload error',
      uploadTimestamp
    };
  }
};

/**
 * Retrieve file from IPFS using multiple gateways
 */
export const retrieveFromIPFS = async (cid: string): Promise<ArrayBuffer> => {
  try {
    if (!cid) {
      throw new Error('IPFS CID is required');
    }

    // Validate CID format
    if (!validateIPFSCID(cid)) {
      throw new Error('Invalid IPFS CID format');
    }

    // List of IPFS gateways to try in order (most reliable first)
    const gateways = [
      `https://gateway.pinata.cloud/ipfs/${cid}`,
      `https://cloudflare-ipfs.com/ipfs/${cid}`,
      `https://ipfs.io/ipfs/${cid}`,
      `https://dweb.link/ipfs/${cid}`,
      `https://ipfs.algonode.xyz/ipfs/${cid}`,
      `https://ipfs.algonode.dev/ipfs/${cid}`
    ];

    let lastError: Error | null = null;

    // Try each gateway in sequence
    for (const gateway of gateways) {
      try {
        console.log(`Attempting to retrieve from gateway: ${gateway}`);
        
        const response = await fetch(gateway, {
          signal: AbortSignal.timeout(15000) // 15 second timeout per gateway
        });

        if (response.ok) {
          console.log(`Successfully retrieved from gateway: ${gateway}`);
          return await response.arrayBuffer();
        }

        lastError = new Error(`Gateway ${gateway} returned ${response.status}: ${response.statusText}`);
        console.warn(lastError.message);
      } catch (error: any) {
        lastError = error;
        console.warn(`Gateway ${gateway} failed:`, error.message);
        continue;
      }
    }

    throw lastError || new Error('All IPFS gateways failed');

  } catch (error: any) {
    console.error('IPFS retrieval error:', error);
    throw new Error(`Failed to retrieve file from IPFS: ${error.message}`);
  }
};

/**
 * Validate IPFS CID format
 */
export const validateIPFSCID = (cid: string): boolean => {
  // Basic CID validation (simplified) - supports both CIDv0 and CIDv1
  const cidRegex = /^(Qm[1-9A-HJ-NP-Za-km-z]{44}|baf[a-z0-9]{56}|bafk[a-z0-9]{56})$/;
  return cidRegex.test(cid);
};

/**
 * Get file info from Pinata
 */
export const getFileInfo = async (cid: string) => {
  try {
    const pinataJWT = import.meta.env.VITE_PINATA_JWT;
    
    if (!pinataJWT) {
      throw new Error('Pinata JWT not configured');
    }

    const response = await fetch(`https://api.pinata.cloud/data/pinList?hashContains=${cid}`, {
      headers: {
        'Authorization': `Bearer ${pinataJWT}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get file info: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    return result.rows?.[0] || null;
  } catch (error: any) {
    console.error('Error getting file info:', error);
    return null;
  }
};

/**
 * Test Pinata connection and credentials
 */
export const testPinataConnection = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    const pinataJWT = import.meta.env.VITE_PINATA_JWT;
    
    if (!pinataJWT) {
      return { success: false, error: 'Pinata JWT not configured' };
    }

    const response = await fetch('https://api.pinata.cloud/data/testAuthentication', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${pinataJWT}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      return { 
        success: false, 
        error: `Authentication failed: ${response.status} ${response.statusText}. ${errorText}` 
      };
    }

    const result = await response.json();
    
    if (result.message === 'Congratulations! You are communicating with the Pinata API!') {
      return { success: true };
    }

    return { success: false, error: 'Unexpected response from Pinata API' };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};